import 'whatwg-fetch';
import Basil from 'basil.js';
import get from 'lodash/get';

import cacheBuster from './cacheBuster';
import storage from './storage';
import config from 'config';

class RequestService {
  constructor(protocol, domain, root) {
    this._resetOptions();

    this.jwtSuffix = config.ENVIRONMENT === 'Production' ? '' : '-dev';

    this.legacyApiUrl = `${config.APP_PROTOCOL}://${config.APP_URL}/api/v2`;
  }

  get(url, requiresAuth = true) {
    return this._request(url, 'GET', requiresAuth);
  }

  put(url, body, requiresAuth = true) {
    return this._request(url, 'PUT', requiresAuth, body);
  }

  post(url, body, requiresAuth = true) {
    return this._request(url, 'POST', requiresAuth, body);
  }

  async postFormData(url, formData) {
    let response = await this._requestHelper(url, 'POST', formData, false, true);

    this._resetOptions();

    return response;
  }

  patch(url, body, requiresAuth = true, nonPublicScope) {
    return this._request(url, 'PATCH', requiresAuth, body, nonPublicScope);
  }

  delete(url, requiresAuth = true) {
    return this._request(url, 'DELETE', requiresAuth);
  }

  getVersion() {
    this._resetOptions();

    return this._requestHelper(`/version.json?${cacheBuster()}`, 'GET', false);
  }

  _request(url, method, requiresAuth, body = null, nonPublicScope = false) {
    this._resetOptions();

    if (!requiresAuth) {
      return this._requestHelper(url, method, body);
    } else if (storage.getDomainCookie(`client-jwt${this.jwtSuffix}`)) {
      this.options.headers.Authorization = `Bearer ${storage.getDomainCookie(`client-jwt${this.jwtSuffix}`)}`;
      return this._requestHelper(url, method, body, true, false, nonPublicScope);
    } else {
      return Promise.reject({ requiresLogin: true });
    }
  }

  async _requestHelper(url, method, body, isJSON = true, noOptions = false, nonPublicScope = false) {
    let options = noOptions ? {} : this.options;

    if (method) options.method = method;
    if (body) options.body = isJSON ? JSON.stringify(body) : body;

    let response = await window.fetch(url, options);
    response = await this._resStatus(response, nonPublicScope);
    response = await this._resJson(response);

    return response;
  }

  _resStatus(response, nonPublicScope) {
    if (response.status >= 200 && response.status < 300) {
      return Promise.resolve(response)
    } else if (response.status === 401 || response.status === 403) {
      return Promise.reject({
        requiresLogin: true,
        nonPublicScope,
      });
    } else {
      return Promise.reject(response)
    }
  }

  async _resJson(response) {
    let text = await response.text();

    let results = text ? JSON.parse(text) : {};

    if (get(results, 'data.token')) storage.setDomainCookie(`client-jwt${this.jwtSuffix}`, results.data.token, config.APP_DOMAIN);
    else if (get(results, 'data.jwt')) storage.setDomainCookie(`client-jwt${this.jwtSuffix}`, results.data.jwt, config.APP_DOMAIN);
    else if (results.jwt) storage.setDomainCookie(`client-jwt${this.jwtSuffix}`, results.jwt, config.APP_DOMAIN);

    return results;
  }

  _resetOptions() {
    this.options = {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      mode: 'cors',
    };
  }

  async loginLegacy(email_address, password, token) {
    let payload = {
      email_address,
      password,
    };

    this._resetOptions();

    if (token) payload.token = token;

    try {
      this.options.credentials = 'include';
      this.options.headers['Content-Type'] = 'application/x-www-form-urlencoded';

      let response = await this._requestHelper(`${this.legacyApiUrl}/token`, 'POST', 'grant_type=client_credentials&client_id=frontend&client_secret=frontend&scope=public', false);

      this.options.headers['Content-Type'] = 'application/json';
      this.options.headers.Authorization = `Bearer ${response.access_token}`;

      response = await this._requestHelper(`${this.legacyApiUrl}/auth/login`, 'POST', payload);

      this._resetOptions();

      return response;
    } catch (error) {
      throw error;
    }
  }

  async logoutLegacy() {
    let isDevelopment = config.ENVIRONMENT === 'Development';

    if (isDevelopment) {
      return Promise.resolve();
    } else {
      this._resetOptions();

      try {
        return await this.get(`${this.legacyApiUrl}/auth/logout`);
      } catch (e) {
        return Promise.resolve();
      }
    }
  }
};

export default new RequestService();